import React, { FC, useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { IKnockStatus } from '@user/store/knockStatus/typings'
import { KnockStatusIcon } from '@user/components/KnockStatus'
import { MapPinBody } from '@user/components/HereMap/MapPin/components'
import { MapPinTypeVariant } from '@user/components/HereMap/MapPin/types'

const DEFAULT_SYSTEM_STATUS: IKnockStatus = {
  id: 'system-do-not-knock',
  name: 'Do not knock',
  color: '#D63B1F',
  icon: 16,
  isSystem: true,
}

const PIN_VARIANTS = [
  MapPinTypeVariant.Lead,
  MapPinTypeVariant.Opportunity,
  MapPinTypeVariant.PendingLead,
  MapPinTypeVariant.Lead,
  MapPinTypeVariant.Opportunity,
  MapPinTypeVariant.KnockStatus,
]

const PIN_WITH_BADGE_POSITIONS = [
  { x: 10, y: 50 },
  { x: 35, y: 70 },
  { x: 55, y: 45 },
  { x: 75, y: 65 },
  { x: 90, y: 40 },
]

const BADGE_ONLY_POSITIONS = [
  { x: 25, y: 30 },
  { x: 50, y: 80 },
  { x: 70, y: 25 },
  { x: 15, y: 75 },
  { x: 85, y: 55 },
]

type MapItem = {
  key: string
  x: number
  y: number
  status: IKnockStatus
  showPin: boolean
  pinVariant?: MapPinTypeVariant
}

interface MapPreviewProps {
  statuses: IKnockStatus[]
}

export const MapPreview: FC<MapPreviewProps> = ({ statuses }) => {
  const displayStatuses = useMemo(() => {
    const systemStatus = statuses.find((s) => s.isSystem)
    const customStatuses = statuses.filter((s) => !s.isSystem)

    const resolvedSystemStatus = systemStatus || DEFAULT_SYSTEM_STATUS

    return [resolvedSystemStatus, ...customStatuses]
  }, [statuses])

  const itemsToRender = useMemo(() => {
    if (displayStatuses.length === 0) return []

    const items: MapItem[] = []
    let pinIndex = 0
    let badgeIndex = 0

    displayStatuses.forEach((status, statusIndex) => {
      if (status.isSystem) {
        // System status ("Do not knock") shows BOTH: badge only AND pin with badge
        if (badgeIndex < BADGE_ONLY_POSITIONS.length) {
          items.push({
            key: `badge-${status.id}`,
            ...BADGE_ONLY_POSITIONS[badgeIndex],
            status,
            showPin: false,
          })
          badgeIndex++
        }
        if (pinIndex < PIN_WITH_BADGE_POSITIONS.length) {
          items.push({
            key: `pin-${status.id}`,
            ...PIN_WITH_BADGE_POSITIONS[pinIndex],
            status,
            showPin: true,
            pinVariant: PIN_VARIANTS[pinIndex % PIN_VARIANTS.length],
          })
          pinIndex++
        }
      } else {
        const useBadgeOnly = statusIndex % 2 === 0
        if (useBadgeOnly && badgeIndex < BADGE_ONLY_POSITIONS.length) {
          items.push({
            key: `badge-${status.id}`,
            ...BADGE_ONLY_POSITIONS[badgeIndex],
            status,
            showPin: false,
          })
          badgeIndex++
        } else if (
          !useBadgeOnly &&
          pinIndex < PIN_WITH_BADGE_POSITIONS.length
        ) {
          items.push({
            key: `pin-${status.id}`,
            ...PIN_WITH_BADGE_POSITIONS[pinIndex],
            status,
            showPin: true,
            pinVariant: PIN_VARIANTS[pinIndex % PIN_VARIANTS.length],
          })
          pinIndex++
        } else if (pinIndex < PIN_WITH_BADGE_POSITIONS.length) {
          items.push({
            key: `pin-${status.id}`,
            ...PIN_WITH_BADGE_POSITIONS[pinIndex],
            status,
            showPin: true,
            pinVariant: PIN_VARIANTS[pinIndex % PIN_VARIANTS.length],
          })
          pinIndex++
        } else if (badgeIndex < BADGE_ONLY_POSITIONS.length) {
          items.push({
            key: `badge-${status.id}`,
            ...BADGE_ONLY_POSITIONS[badgeIndex],
            status,
            showPin: false,
          })
          badgeIndex++
        }
      }
    })

    return items
  }, [displayStatuses])

  const getPinBgColor = (variant: MapPinTypeVariant) => {
    switch (variant) {
      case MapPinTypeVariant.PendingLead:
        return '#F9BA1B'
      case MapPinTypeVariant.Opportunity:
        return '#31805B'
      case MapPinTypeVariant.Lead:
        return '#1565C0'
      default:
        return '#1565C0'
    }
  }

  return (
    <Box>
      <Typography variant="subtitle3" sx={{ mb: 1, display: 'block' }}>
        Map Preview
      </Typography>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: 280,
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid #E0E0E0',
        }}
      >
        <Box
          component="img"
          src="/static-assets/images/map-preview-background.svg"
          alt="Map preview background"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />

        {itemsToRender.map((item) => (
          <Box
            key={item.key}
            sx={{
              position: 'absolute',
              left: `${item.x}%`,
              top: `${item.y}%`,
              transform: item.showPin
                ? 'translate(-50%, -100%)'
                : 'translate(-50%, -50%)',
              zIndex: 1,
            }}
          >
            {item.showPin && item.pinVariant ? (
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ zIndex: 2 }}>
                  <KnockStatusIcon
                    color={item.status.color}
                    icon={item.status.icon}
                    width={35}
                    height={35}
                    viewBox="0 0 50 50"
                  />
                </Box>
                <Box sx={{ marginTop: '-4px', zIndex: 1 }}>
                  <MapPinBody
                    size={25}
                    bgColor={getPinBgColor(item.pinVariant)}
                    iconColor="white"
                    variant={item.pinVariant}
                    iconSize={10}
                    count={1}
                    multitenant={false}
                  />
                </Box>
              </Box>
            ) : (
              <KnockStatusIcon
                color={item.status.color}
                icon={item.status.icon}
                width={35}
                height={35}
                viewBox="0 0 50 50"
              />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  )
}
